//Stores
import { get } from 'svelte/store';
import { devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
//Helpers
import { getVComponentValue } from '../virtual_helpers/virtual_components_status_helpers';
import {
	getStatusIconByRole,
	getTemplateComponents,
	getTemplateService,
	translateTemplateTitle,
} from './template_helpers';
import { getVCIcon } from 'src/components/Scenes/helpers/virtual_components/virtual_component_utils';
import { updateIrrigationControllerStatus } from './irrigation/irrigation_helpers';
import { updateDehumidifierStatus } from './dehumidifier/dehumidifier_helpers';
import { updateNeoWaterValveStatus } from './valve/neo_valve_helpers';
import { updateEvChargerStatus } from './ev_charger/ev_charger_helpers';
import { updateSimpleWaterValveStatus } from './valve/simple_water_valve_helpers';
import { updateThermostatsStatus } from './thermostat/thermostat_helpers';
import { updateHeimanCo2Sensor } from './sensor/heiman_co2_sensor';
import { updateWindowCoverStatus } from './cover/window_cover_helpers';
import { updateHoermannDoorStatus, updateHeatPumpStatus } from '@shelly/device-templates';
import { convertToUnit } from '../units_helpers';
//Types
import type {
	TemplateComponent,
	TemplateService,
	VirtualComponentInterfaceData,
	VCInterfaceStatus,
	VirtualComponentTypes,
	TemplateType,
	ServiceType,
} from 'src/types/VirtualComponents';
import type { Device } from '../../types/Device';

export const getTemplateTypeByService = (serviceType: ServiceType): TemplateType => {
	const templateType = serviceType.split(':')[0];
	switch (templateType) {
		case 'valve':
		case 'simple-water-valve-controller':
			return 'valve';
		case 'cover':
		case 'window-cover':
			return 'cover';
		case 'irrigation-controller':
			return 'irrigation';
		case 'linkedgo-st1820-floor-thermostat':
		case 'linkedgo-st-802-hvac':
		case 'thermostat':
			return 'thermostat';
		case 'neo-water-valve-advanced':
			return 'neo-valve';
		case 'heiman-co2-sensor':
			return 'co2-sensor';
		case 'top-evse-charger':
			return 'ev-charger';
		case 'dehumidifier':
			return 'dehumidifier';
		default:
			return templateType as TemplateType;
	}
};

/**
 * Returns the virtual component statuses for a XT1 device.
 *
 * @param deviceID - The ID of the device.
 * @returns An array of virtual component interface statuses.
 */
export const getXT1TemplateStatus = (deviceID: Device['id']): VCInterfaceStatus[] => {
	const interface_data: VCInterfaceStatus[] = [];
	const device = get(devices)?.[deviceID];
	if (!device) return interface_data;
	const templateService: TemplateService = getTemplateService(device);
	const components: TemplateComponent[] = getTemplateComponents(device);
	const serviceType: ServiceType = templateService.type;
	const templateType: TemplateType = getTemplateTypeByService(serviceType);
	const deviceStatus = devices_status.getByRealId(deviceID);

	for (let i = 0; i < components.length; ++i) {
		const item: TemplateComponent = components[i];
		const type: VirtualComponentTypes = item.type;
		const role = item.role;
		const name = item.config?.name;
		const valueData: VirtualComponentInterfaceData = getVComponentValue(
			deviceStatus,
			item.component,
			type,
			item.config,
		);
		const commonData: VCInterfaceStatus = {
			...valueData,
			id: item.config?.id,
			name: role || item.component,
			role,
			vcName: name,
			vcType: type,
			templateType,
			serviceType,
			hasAction:
				type == 'button' || (type == 'boolean' && item?.config?.meta?.ui?.view == 'toggle'),
			rawValue: valueData.value,
			value:
				type == 'object'
					? valueData.value
					: translateTemplateTitle(valueData.label) ||
						convertToUnit(valueData.value, valueData.unit || ''),
			icon: getStatusIconByRole(role) || valueData.icon,
			config: item.config,
			component: item.component,
			hideLabel: type == 'object' && !/^(water_consumption|phase_info)$/.test(role),
			mainLabel: false,
		};
		switch (templateType) {
			case 'valve':
				updateSimpleWaterValveStatus(interface_data, commonData, valueData);
				break;
			case 'cover':
				updateWindowCoverStatus(interface_data, commonData, valueData);
				break;
			case 'irrigation':
				updateIrrigationControllerStatus(
					interface_data,
					commonData,
					item.type,
					valueData,
					templateService,
					deviceStatus,
				);
				break;
			case 'thermostat':
				updateThermostatsStatus(interface_data, commonData, valueData);
				break;
			case 'neo-valve':
				updateNeoWaterValveStatus(interface_data, commonData, valueData, templateService);
				break;
			case 'co2-sensor':
				updateHeimanCo2Sensor(interface_data, commonData, valueData);
				break;
			case 'ev-charger':
				updateEvChargerStatus(interface_data, commonData, valueData);
				break;
			case 'dehumidifier':
				updateDehumidifierStatus(interface_data, commonData, valueData);
				break;
			case 'hoermann':
				updateHoermannDoorStatus(interface_data, commonData, valueData);
				break;
			case 'heatpump':
				updateHeatPumpStatus(interface_data, commonData, valueData);
				break;
			default:
				if (i === 0 && !commonData.isBleButton) commonData.mainLabel = true;
				if (commonData.hasAction) commonData.hideLabel = true;
				if (typeof valueData.value !== 'object') {
					updateTemplateControllerStatus(interface_data, commonData, valueData);
				}
		}
	}
	if (templateType === 'co2-sensor') {
		interface_data.reverse();
	}
	return interface_data;
};

const updateTemplateControllerStatus = (
	interface_data: VCInterfaceStatus[],
	data: VCInterfaceStatus,
	valueData: VirtualComponentInterfaceData,
): void => {
	const { config } = data;
	data.rawValue = valueData.value;
	data.name = config?.name || data.name;
	data.vcName = data.name;
	data.unit = config?.meta?.ui?.unit || '';
	data.hideLabel =
		data.hideLabel || config?.meta?.ui?.view === '' || config?.meta?.ui?.view === 'hidden';
	data.icon = getVCIcon(config);
	interface_data.push(data);
};
